import * as THREE from 'three';
import { createCar, updateCarVisual } from './carFactory.js';
import { createProjectileModel } from './effects.js';
import { CARS } from './data.js';
import codemaneLionUrl from '../assets/brand/codemane-lion.png';
import turboCrownLogoUrl from '../assets/brand/the-turbo-crown-logo.png';

const FIXED_DT=1/60;
const SCENE_DURATION=18.5;
const IMPACT_TIME=2.08;
const DRIFT_START=2.35;
const JUMP_START=5.95;
const SLOW_MOTION_START=6.25;
const BOOST_TIME=8.35;
const AIR_CHASE_START=9.05;
const LAND_TIME=13.55;
const OUTRO_START=15.35;
const PLATE_Z=14;
const tempPosition=new THREE.Vector3();
const tempNextPosition=new THREE.Vector3();
const tempTarget=new THREE.Vector3();
const tempCamera=new THREE.Vector3();
const tempDirection=new THREE.Vector3();
const tempDirectionNext=new THREE.Vector3();
const projectileForward=new THREE.Vector3(0,0,-1);
const driftPointA=new THREE.Vector3(0,.03,13.72);
const driftPointB=new THREE.Vector3(-.25,.03,7.8);
const driftPointC=new THREE.Vector3(4.2,.03,5.5);
const driftPointD=new THREE.Vector3(4.65,.03,-1.45);

const clamp01=(value)=>THREE.MathUtils.clamp(value,0,1);
const smooth=(value)=>{
  const t=clamp01(value);
  return t*t*(3-2*t);
};
const easeInCubic=(value)=>clamp01(value)**3;
const cubicBezier=(a,b,c,d,t,target)=>{
  const inverse=1-t;
  return target.set(0,0,0)
    .addScaledVector(a,inverse*inverse*inverse)
    .addScaledVector(b,3*inverse*inverse*t)
    .addScaledVector(c,3*inverse*t*t)
    .addScaledVector(d,t*t*t);
};

function seededRandom(seed=0x71c4e29){
  let state=seed>>>0;
  return ()=>{
    state=(Math.imul(state,1664525)+1013904223)>>>0;
    return state/4294967296;
  };
}

function setShadows(root){
  root.traverse((child)=>{
    if(!child.isMesh||child.userData.noShadow)return;
    child.castShadow=true;
    child.receiveShadow=true;
  });
}

function titleTexture(){
  const canvas=document.createElement('canvas');
  canvas.width=2048;
  canvas.height=768;
  const context=canvas.getContext('2d');
  const gradient=context.createLinearGradient(0,0,0,canvas.height);
  gradient.addColorStop(0,'#dfe7ed');
  gradient.addColorStop(.45,'#828f9c');
  gradient.addColorStop(.52,'#edf4f7');
  gradient.addColorStop(1,'#596570');
  context.fillStyle=gradient;
  context.fillRect(0,0,canvas.width,canvas.height);
  context.globalAlpha=.16;
  for(let y=4;y<canvas.height;y+=7){
    context.fillStyle=y%14?'#ffffff':'#1b2229';
    context.fillRect(0,y,canvas.width,1);
  }
  context.globalAlpha=1;
  context.strokeStyle='rgba(255,255,255,.46)';
  context.lineWidth=7;
  context.strokeRect(28,28,canvas.width-56,canvas.height-56);
  context.textAlign='center';
  context.textBaseline='middle';
  context.shadowColor='rgba(0,0,0,.72)';
  context.shadowBlur=26;
  context.shadowOffsetY=16;
  const titleX=canvas.width*.66;
  context.fillStyle='#f7fafc';
  context.font='900 italic 116px Impact, Arial Black, sans-serif';
  context.letterSpacing='10px';
  context.fillText('CODEMANE',titleX,canvas.height*.37);
  context.shadowBlur=14;
  context.shadowOffsetY=9;
  context.fillStyle='#f7fafc';
  context.font='900 italic 72px Impact, Arial Black, sans-serif';
  context.letterSpacing='20px';
  context.fillText('GAMES',titleX,canvas.height*.54);
  context.shadowBlur=13;
  context.shadowOffsetY=8;
  context.fillStyle='#ffd13d';
  context.font='900 52px Arial Black, sans-serif';
  context.letterSpacing='19px';
  context.fillText('PRESENTS',titleX,canvas.height*.71);
  const texture=new THREE.CanvasTexture(canvas);
  texture.colorSpace=THREE.SRGBColorSpace;
  texture.anisotropy=8;
  return texture;
}

function radialTexture(){
  const canvas=document.createElement('canvas');
  canvas.width=256;
  canvas.height=256;
  const context=canvas.getContext('2d');
  const gradient=context.createRadialGradient(128,128,0,128,128,128);
  gradient.addColorStop(0,'rgba(255,248,194,1)');
  gradient.addColorStop(.16,'rgba(255,195,56,.95)');
  gradient.addColorStop(.46,'rgba(255,102,28,.42)');
  gradient.addColorStop(1,'rgba(255,92,20,0)');
  context.fillStyle=gradient;
  context.fillRect(0,0,256,256);
  const texture=new THREE.CanvasTexture(canvas);
  texture.colorSpace=THREE.SRGBColorSpace;
  return texture;
}

function softSmokeTexture(){
  const canvas=document.createElement('canvas');
  canvas.width=128;
  canvas.height=128;
  const context=canvas.getContext('2d');
  const gradient=context.createRadialGradient(64,64,4,64,64,62);
  gradient.addColorStop(0,'rgba(255,255,255,.8)');
  gradient.addColorStop(.38,'rgba(255,255,255,.48)');
  gradient.addColorStop(1,'rgba(255,255,255,0)');
  context.fillStyle=gradient;
  context.fillRect(0,0,128,128);
  const texture=new THREE.CanvasTexture(canvas);
  texture.colorSpace=THREE.SRGBColorSpace;
  return texture;
}

function meshMaterial(color,{metalness=.1,roughness=.5,emissive=0x000000}={}){
  return new THREE.MeshPhysicalMaterial({
    color,
    metalness,
    roughness,
    clearcoat:.5,
    clearcoatRoughness:.22,
    emissive,
  });
}

export class TrailerDirector {
  constructor(game,{carId,loadout,initialTime=0}={}){
    this.game=game;
    this.scene=game.scene;
    this.camera=game.camera;
    this.renderer=game.renderer;
    this.carId=carId||game.selectedCarId;
    this.loadout=loadout||game.selectedLoadout;
    this.initialTime=THREE.MathUtils.clamp(Number(initialTime)||0,0,SCENE_DURATION);
    this.group=new THREE.Group();
    this.group.name='trailer-director-scene-01';
    this.playing=true;
    this.ready=false;
    this.time=0;
    this.accumulator=0;
    this.distance=0;
    this.lastCarPosition=new THREE.Vector3();
    this.impactTriggered=false;
    this.boostClock=0;
    this.shards=[];
    this.sparks=[];
    this.smoke=[];
    this.driftPuffs=[];
    this.speedLines=[];
    this.followers=[];
    this.missiles=[];
    this.primaryMissile=null;
    this.warningTriggered=false;
    this.boostTriggered=false;
    this.recording=false;
    this.recordedChunks=[];
    this.mediaRecorder=null;
    this.captureAudioDestination=null;
    this.restoreExposure=game.renderer.toneMappingExposure;
    this.restoreSunIntensity=game.sun.intensity;
    this.restoreSunPosition=game.sun.position.clone();
    this.boundKeyDown=(event)=>this.onKeyDown(event);
  }

  start(){
    if(this.game.selectedMapId!=='living')this.game.setMap('living');
    if(this.game.showcaseCar){
      this.scene.remove(this.game.showcaseCar);
      this.game.showcaseCar=null;
    }
    this.game.effects.clear();
    this.game.mode='trailer';
    this.game.trailerDirector=this;
    this.renderer.toneMappingExposure=1.08;
    this.game.sun.intensity=4.1;
    this.game.sun.position.set(18,34,26);
    this.game.sun.target.position.set(0,0,7);
    this.scene.add(this.group);
    this.buildCinematicSet();
    this.buildCar();
    this.buildControls();
    this.reset();
    if(this.ready)this.applyInitialTime();
    this.game.audio.pauseMusic(true);
    window.addEventListener('keydown',this.boundKeyDown);
  }

  buildCar(){
    this.car=createCar(this.carId,{scale:1.08,loadout:this.loadout});
    this.car.name='trailer-hero-car';
    setShadows(this.car);
    this.group.add(this.car);
    const followerIds=CARS
      .map((car)=>car.id)
      .filter((id)=>id!==this.carId)
      .slice(0,4);
    while(followerIds.length<4)followerIds.push(this.carId);
    const followerColors=['#2f7ee8','#e84552','#f0b92d','#8b55df'];
    const followerAccents=['#dff4ff','#fff1d4','#27213a','#b9ffec'];
    followerIds.forEach((id,index)=>{
      const car=createCar(id,{
        scale:.98,
        loadout:this.loadout,
        color:followerColors[index],
        accent:followerAccents[index],
      });
      car.name=`trailer-pursuer-${index+1}`;
      car.visible=false;
      setShadows(car);
      this.group.add(car);
      this.followers.push({
        car,
        index,
        delay:.42+index*.2,
        lateral:(index%2?-1:1)*(1.15+Math.floor(index/2)*.7),
        distance:0,
        lastPosition:new THREE.Vector3(),
        nextPosition:new THREE.Vector3(),
      });
    });
    this.buildMissiles();
    const pending=[this.car,...this.followers.map((follower)=>follower.car)]
      .map((car)=>car.userData.assetReady)
      .filter((promise)=>promise?.then);
    if(pending.length){
      this.setStatus('CARGANDO AUTO PUBLICADO…');
      this.playing=false;
      Promise.allSettled(pending).finally(()=>{
        this.ready=true;
        this.playing=true;
        this.setStatus('ESCENA LISTA');
        this.reset();
        this.applyInitialTime();
      });
    }else{
      this.ready=true;
      this.setStatus('ESCENA LISTA');
    }
  }

  applyInitialTime(){
    if(this.initialTime<=0)return;
    const target=this.initialTime;
    this.initialTime=0;
    this.seek(target);
    this.playing=false;
    this.playButton.textContent='▶ REPRODUCIR';
    this.setStatus('VISTA PREVIA');
  }

  buildMissiles(){
    const projectileId=this.car.userData.projectileId;
    const definitions=[
      {launch:6.55,duration:2.15,lateral:.7,vertical:.12,primary:true},
      {launch:9.12,duration:2.25,lateral:-1.25,vertical:.55},
      {launch:9.48,duration:2.05,lateral:1.65,vertical:-.05},
      {launch:9.92,duration:2.25,lateral:-2.05,vertical:.2},
      {launch:10.34,duration:2.05,lateral:2.3,vertical:.8},
      {launch:10.78,duration:2.1,lateral:-1.05,vertical:1.05},
      {launch:11.2,duration:1.95,lateral:1.2,vertical:-.12},
      {launch:11.62,duration:1.8,lateral:-2.45,vertical:.5},
    ];
    definitions.forEach((definition,index)=>{
      const root=createProjectileModel(projectileId,this.car.userData.paintFinish);
      root.name=`trailer-missile-${index+1}`;
      root.scale.multiplyScalar(definition.primary ? .62 : .46);
      root.visible=false;
      root.traverse((child)=>{
        if(!child.isMesh)return;
        child.castShadow=true;
        child.receiveShadow=true;
      });
      const trail=new THREE.Mesh(
        new THREE.ConeGeometry(definition.primary?.12:.09,definition.primary?1.25:.9,12,1,true),
        new THREE.MeshBasicMaterial({
          color:index%2?0xffa632:0x56dfff,
          transparent:true,
          opacity:.72,
          depthWrite:false,
          blending:THREE.AdditiveBlending,
          side:THREE.DoubleSide,
        }),
      );
      trail.rotation.x=Math.PI/2;
      trail.position.z=.58;
      root.add(trail);
      this.group.add(root);
      const missile={
        root,
        trail,
        index,
        ...definition,
        position:new THREE.Vector3(),
        lastPosition:new THREE.Vector3(),
        scratchA:new THREE.Vector3(),
        scratchB:new THREE.Vector3(),
        scratchC:new THREE.Vector3(),
        scratchD:new THREE.Vector3(),
        scratchNext:new THREE.Vector3(),
      };
      this.missiles.push(missile);
      if(definition.primary)this.primaryMissile=missile;
    });
  }

  buildCinematicSet(){
    const plateTexture=titleTexture();
    this.plateMaterial=new THREE.MeshPhysicalMaterial({
      color:0xffffff,
      map:plateTexture,
      metalness:.82,
      roughness:.24,
      clearcoat:.7,
      clearcoatRoughness:.18,
    });
    this.plate=new THREE.Mesh(new THREE.BoxGeometry(5.7,2.18,.16,2,2,1),this.plateMaterial);
    this.plate.position.set(0,1.24,PLATE_Z);
    this.plate.castShadow=true;
    this.plate.receiveShadow=true;
    this.group.add(this.plate);

    this.plateFrame=new THREE.Group();
    const frameMaterial=meshMaterial('#313842',{metalness:.92,roughness:.2});
    [[5.98,.13,0,1.13],[5.98,.13,0,-1.13],[.13,2.38,-2.95,0],[.13,2.38,2.95,0]].forEach(([width,height,x,y])=>{
      const part=new THREE.Mesh(new THREE.BoxGeometry(width,height,.23),frameMaterial);
      part.position.set(x,y,0);
      part.castShadow=true;
      this.plateFrame.add(part);
    });
    const boltMaterial=meshMaterial('#e8edf0',{metalness:1,roughness:.16});
    [[-2.7,.92],[2.7,.92],[-2.7,-.92],[2.7,-.92]].forEach(([x,y])=>{
      const bolt=new THREE.Mesh(new THREE.CylinderGeometry(.075,.075,.055,16),boltMaterial);
      bolt.rotation.x=Math.PI/2;
      bolt.position.set(x,y,-.14);
      this.plateFrame.add(bolt);
    });
    const lionTexture=new THREE.TextureLoader().load(codemaneLionUrl);
    lionTexture.colorSpace=THREE.SRGBColorSpace;
    lionTexture.anisotropy=8;
    this.codemaneLogo=new THREE.Mesh(
      new THREE.PlaneGeometry(1.28,1.42),
      new THREE.MeshBasicMaterial({
        map:lionTexture,
        transparent:true,
        alphaTest:.04,
        depthWrite:false,
        side:THREE.DoubleSide,
        toneMapped:false,
      }),
    );
    this.codemaneLogo.position.set(1.82,0,-.14);
    this.codemaneLogo.renderOrder=3;
    this.plateFrame.add(this.codemaneLogo);
    this.plateFrame.position.copy(this.plate.position);
    this.group.add(this.plateFrame);

    const random=seededRandom();
    const columns=8;
    const rows=4;
    const shardWidth=5.65/columns;
    const shardHeight=2.12/rows;
    for(let row=0;row<rows;row+=1){
      for(let column=0;column<columns;column+=1){
        const width=shardWidth*(.72+random()*.23);
        const height=shardHeight*(.7+random()*.25);
        const geometry=new THREE.BoxGeometry(width,height,.11);
        const material=meshMaterial(
          new THREE.Color().setHSL(.58,.08,.47+random()*.25),
          {metalness:.9,roughness:.22+random()*.16},
        );
        const mesh=new THREE.Mesh(geometry,material);
        const x=-5.65*.5+shardWidth*(column+.5)+(random()-.5)*.1;
        const y=.2+shardHeight*(row+.5)+(random()-.5)*.07;
        mesh.position.set(x,y,PLATE_Z);
        mesh.visible=false;
        mesh.castShadow=true;
        this.group.add(mesh);
        this.shards.push({
          mesh,
          origin:mesh.position.clone(),
          velocity:new THREE.Vector3(),
          spin:new THREE.Vector3(),
          random:[random(),random(),random(),random(),random(),random()],
        });
      }
    }

    const sparkGeometry=new THREE.IcosahedronGeometry(.045,0);
    for(let index=0;index<58;index+=1){
      const material=new THREE.MeshBasicMaterial({
        color:index%4===0?0xffffff:index%2?0xffca3d:0xff6a24,
        transparent:true,
        opacity:0,
        depthWrite:false,
        blending:THREE.AdditiveBlending,
      });
      const mesh=new THREE.Mesh(sparkGeometry,material);
      mesh.visible=false;
      this.group.add(mesh);
      this.sparks.push({
        mesh,
        velocity:new THREE.Vector3(),
        life:0,
        maxLife:.45+random()*.55,
        random:[random(),random(),random(),random()],
      });
    }

    const smokeGeometry=new THREE.SphereGeometry(.24,10,7);
    for(let index=0;index<14;index+=1){
      const mesh=new THREE.Mesh(smokeGeometry,new THREE.MeshBasicMaterial({
        color:index%3===0?0x9da2a8:0x505661,
        transparent:true,
        opacity:0,
        depthWrite:false,
      }));
      mesh.visible=false;
      this.group.add(mesh);
      this.smoke.push({mesh,random:[random(),random(),random(),random()]});
    }
    const driftTexture=softSmokeTexture();
    for(let index=0;index<20;index+=1){
      const mesh=new THREE.Sprite(new THREE.SpriteMaterial({
        map:driftTexture,
        color:index%3===0?0xb9bec6:0x8e949e,
        transparent:true,
        opacity:0,
        depthWrite:false,
      }));
      mesh.visible=false;
      this.group.add(mesh);
      this.driftPuffs.push({
        mesh,
        spawn:DRIFT_START+.18+index*.17,
        side:index%2?-1:1,
        phase:random()*Math.PI*2,
        origin:new THREE.Vector3(),
      });
    }

    this.impactFlash=new THREE.Sprite(new THREE.SpriteMaterial({
      map:radialTexture(),
      color:0xffb23f,
      transparent:true,
      opacity:0,
      depthWrite:false,
      blending:THREE.AdditiveBlending,
    }));
    this.impactFlash.position.set(0,.8,PLATE_Z-.35);
    this.impactFlash.scale.setScalar(.1);
    this.group.add(this.impactFlash);

    this.nitroLight=new THREE.PointLight(0x39d8ff,0,8,2);
    this.group.add(this.nitroLight);
    this.impactLight=new THREE.PointLight(0xff8b2e,0,13,2);
    this.impactLight.position.set(0,1,PLATE_Z-1);
    this.group.add(this.impactLight);

    const crownTexture=new THREE.TextureLoader().load(turboCrownLogoUrl);
    crownTexture.colorSpace=THREE.SRGBColorSpace;
    crownTexture.anisotropy=8;
    this.outroBackdrop=new THREE.Mesh(
      new THREE.PlaneGeometry(5.4,3.2),
      new THREE.MeshBasicMaterial({
        color:0x05040a,
        transparent:true,
        opacity:0,
        depthTest:false,
        depthWrite:false,
        side:THREE.DoubleSide,
        toneMapped:false,
      }),
    );
    this.outroBackdrop.visible=false;
    this.outroBackdrop.renderOrder=9998;
    this.group.add(this.outroBackdrop);
    this.outroLogo=new THREE.Mesh(
      new THREE.PlaneGeometry(1.3,.87),
      new THREE.MeshBasicMaterial({
        map:crownTexture,
        transparent:true,
        opacity:0,
        depthTest:false,
        depthWrite:false,
        side:THREE.DoubleSide,
        toneMapped:false,
      }),
    );
    this.outroLogo.visible=false;
    this.outroLogo.renderOrder=9999;
    this.group.add(this.outroLogo);

    this.addTrackDressing();
  }

  addTrackDressing(){
    const random=seededRandom(0xa93dc221);
    const blockColors=['#e94f58','#f2c744','#3f8ad8','#55b578','#9d64d8'];
    for(let index=0;index<22;index+=1){
      const side=index%2?-1:1;
      const z=10-index*1.25+(random()-.5)*.5;
      const mesh=new THREE.Mesh(
        new THREE.BoxGeometry(.55+random()*.28,.35+random()*.35,.55+random()*.28),
        meshMaterial(blockColors[index%blockColors.length],{roughness:.38}),
      );
      mesh.position.set(side*(6.2+random()*1.8),mesh.geometry.parameters.height*.5+.04,z);
      mesh.rotation.y=random()*.8;
      mesh.castShadow=true;
      mesh.receiveShadow=true;
      this.group.add(mesh);
    }
    const bookColors=['#ef5e67','#4d87d3','#e6bb43','#5aa478'];
    [-1,1].forEach((side)=>{
      for(let index=0;index<5;index+=1){
        const book=new THREE.Mesh(
          new THREE.BoxGeometry(2.7-index*.08,.22,.95),
          meshMaterial(bookColors[index%bookColors.length],{roughness:.62}),
        );
        book.position.set(side*8.2,.15+index*.23,3+side*1.2);
        book.rotation.y=side*(.05+index*.018);
        book.castShadow=true;
        this.group.add(book);
      }
    });

    const lineGeometry=new THREE.BoxGeometry(.025,.018,2.4);
    for(let index=0;index<30;index+=1){
      const line=new THREE.Mesh(lineGeometry,new THREE.MeshBasicMaterial({
        color:index%3===0?0xffd13d:0x52dfff,
        transparent:true,
        opacity:0,
        blending:THREE.AdditiveBlending,
      }));
      line.visible=false;
      this.group.add(line);
      this.speedLines.push({line,index});
    }
  }

  buildControls(){
    const panel=document.createElement('aside');
    panel.className='trailer-director-panel';
    panel.innerHTML=`
      <div class="trailer-director-heading">
        <span>TRAILER DIRECTOR · SECUENCIA COMPLETA</span>
        <strong>PERSECUCIÓN DE LA CORONA</strong>
        <small data-trailer-status>PREPARANDO ESCENA…</small>
      </div>
      <div class="trailer-director-timeline">
        <input data-trailer-timeline type="range" min="0" max="${SCENE_DURATION}" value="0" step="0.01" aria-label="Tiempo de la escena">
        <output data-trailer-time>00.00 / ${SCENE_DURATION.toFixed(2)}</output>
      </div>
      <div class="trailer-director-actions">
        <button data-trailer-play type="button">❚❚ PAUSAR</button>
        <button data-trailer-reset type="button">↻ REINICIAR</button>
        <button data-trailer-record type="button" class="record">● GRABAR 4K</button>
      </div>
      <p>ESPACIO · PLAY/PAUSA &nbsp; R · REINICIAR &nbsp; C · GRABAR</p>
    `;
    const style=document.createElement('style');
    style.textContent=`
      body.trailer-director-mode{background:#05060a;overflow:hidden}
      body.trailer-director-mode #game-canvas{cursor:default}
      body.trailer-director-mode::before,body.trailer-director-mode::after{content:"";position:fixed;z-index:70;left:0;right:0;height:clamp(24px,4.5vh,64px);background:#050509;pointer-events:none}
      body.trailer-director-mode::before{top:0}body.trailer-director-mode::after{bottom:0}
      .trailer-director-panel{position:fixed;z-index:80;left:28px;bottom:calc(clamp(24px,4.5vh,64px) + 18px);width:min(620px,calc(100vw - 56px));padding:16px 18px 13px;border:1px solid rgba(90,223,255,.28);border-radius:18px;background:linear-gradient(135deg,rgba(14,17,31,.94),rgba(25,18,46,.9));box-shadow:0 22px 70px rgba(0,0,0,.55);backdrop-filter:blur(16px);color:#f8f6ff;font-family:Arial,sans-serif}
      .trailer-director-heading{display:grid;gap:3px}.trailer-director-heading span{color:#4ce0f4;font-size:9px;font-weight:900;letter-spacing:2px}.trailer-director-heading strong{font-size:20px;font-weight:1000;font-style:italic}.trailer-director-heading small{color:#ffd33d;font-size:8px;font-weight:900;letter-spacing:1.4px}
      .trailer-director-timeline{display:grid;grid-template-columns:1fr 105px;align-items:center;gap:14px;margin:13px 0 11px}.trailer-director-timeline input{width:100%;accent-color:#ffd33d}.trailer-director-timeline output{color:#b8b5c8;font:800 10px monospace;text-align:right}
      .trailer-director-actions{display:flex;gap:8px}.trailer-director-actions button{min-height:36px;padding:0 14px;border:1px solid rgba(255,255,255,.18);border-radius:10px;background:#24243b;color:#fff;font-size:9px;font-weight:950;letter-spacing:.7px;cursor:pointer}.trailer-director-actions button:hover{border-color:#4ce0f4;background:#30304a}.trailer-director-actions .record{margin-left:auto;border-color:rgba(255,82,99,.55);background:#542235}.trailer-director-actions .record.active{background:#e33e55;box-shadow:0 0 22px rgba(255,71,92,.5)}
      .trailer-director-panel>p{margin:9px 0 0;color:#858296;font-size:8px;font-weight:800;letter-spacing:.5px}
      @media(max-width:700px){.trailer-director-panel{left:12px;width:calc(100vw - 24px);box-sizing:border-box}.trailer-director-actions{flex-wrap:wrap}.trailer-director-actions .record{margin-left:0}}
    `;
    document.head.append(style);
    document.body.append(panel);
    this.panel=panel;
    this.panelStyle=style;
    this.statusElement=panel.querySelector('[data-trailer-status]');
    this.timelineElement=panel.querySelector('[data-trailer-timeline]');
    this.timeElement=panel.querySelector('[data-trailer-time]');
    this.playButton=panel.querySelector('[data-trailer-play]');
    this.recordButton=panel.querySelector('[data-trailer-record]');
    this.playButton.addEventListener('click',()=>this.togglePlay());
    panel.querySelector('[data-trailer-reset]').addEventListener('click',()=>this.reset());
    this.recordButton.addEventListener('click',()=>this.toggleRecording());
    this.timelineElement.addEventListener('input',()=>this.seek(Number(this.timelineElement.value)));
  }

  setStatus(value){
    if(this.statusElement)this.statusElement.textContent=value;
  }

  reset(){
    this.game.effects.clear();
    this.time=0;
    this.accumulator=0;
    this.distance=0;
    this.impactTriggered=false;
    this.warningTriggered=false;
    this.boostTriggered=false;
    this.boostClock=0;
    this.plate.visible=true;
    this.plateFrame.visible=true;
    this.impactFlash.material.opacity=0;
    this.impactFlash.scale.setScalar(.1);
    this.impactLight.intensity=0;
    this.shards.forEach((shard)=>{
      shard.mesh.position.copy(shard.origin);
      shard.mesh.rotation.set(0,0,0);
      shard.mesh.visible=false;
      shard.velocity.set(0,0,0);
      shard.spin.set(0,0,0);
    });
    this.sparks.forEach((spark)=>{
      spark.mesh.visible=false;
      spark.mesh.material.opacity=0;
      spark.life=0;
    });
    this.smoke.forEach(({mesh})=>{
      mesh.visible=false;
      mesh.material.opacity=0;
      mesh.scale.setScalar(1);
    });
    this.driftPuffs.forEach(({mesh})=>{
      mesh.visible=false;
      mesh.material.opacity=0;
      mesh.scale.setScalar(1);
    });
    this.speedLines.forEach(({line})=>{line.visible=false;line.material.opacity=0;});
    this.followers.forEach((follower)=>{
      follower.car.visible=false;
      follower.car.position.set(0,-20,0);
      follower.car.rotation.set(0,0,0);
      follower.distance=0;
      follower.lastPosition.set(0,-20,0);
    });
    this.missiles.forEach((missile)=>{
      missile.root.visible=false;
      missile.root.position.set(0,-20,0);
      missile.lastPosition.set(0,-20,0);
      missile.trail.material.opacity=.72;
    });
    this.outroBackdrop.visible=false;
    this.outroBackdrop.material.opacity=0;
    this.outroLogo.visible=false;
    this.outroLogo.material.opacity=0;
    this.sampleCarPosition(0,this.car.position);
    this.lastCarPosition.copy(this.car.position);
    this.car.rotation.set(0,0,0);
    this.updateCamera(0);
    this.updateInterface();
    this.playing=this.ready;
    this.playButton.textContent=this.playing?'❚❚ PAUSAR':'▶ REPRODUCIR';
    if(this.ready)this.setStatus(this.recording?'GRABANDO 4K · TOMA EN CURSO':'ESCENA LISTA');
  }

  sampleCarPosition(time,target){
    if(time<=.2){
      target.set(0,.03,26.2);
      return target;
    }
    if(time<IMPACT_TIME){
      const progress=easeInCubic((time-.2)/(IMPACT_TIME-.2));
      target.set(0,.03,THREE.MathUtils.lerp(26.2,13.72,progress));
      return target;
    }
    if(time<JUMP_START){
      const progress=clamp01((time-IMPACT_TIME)/(JUMP_START-IMPACT_TIME));
      return cubicBezier(driftPointA,driftPointB,driftPointC,driftPointD,progress,target);
    }
    if(time<BOOST_TIME){
      const progress=clamp01((time-JUMP_START)/(BOOST_TIME-JUMP_START));
      const eased=smooth(progress);
      target.set(
        THREE.MathUtils.lerp(driftPointD.x,5.45,eased),
        .03+2.75*Math.sin(eased*Math.PI*.5),
        THREE.MathUtils.lerp(driftPointD.z,-6.45,eased),
      );
      return target;
    }
    if(time<LAND_TIME){
      const progress=clamp01((time-BOOST_TIME)/(LAND_TIME-BOOST_TIME));
      const eased=smooth(progress);
      target.set(
        THREE.MathUtils.lerp(5.45,2.3,eased)+Math.sin(progress*Math.PI)*.48,
        .05+2.72*(1-eased)+1.22*Math.sin(progress*Math.PI),
        THREE.MathUtils.lerp(-6.45,-26.2,eased),
      );
      return target;
    }
    const groundTime=time-LAND_TIME;
    target.set(
      2.3-groundTime*3.6-Math.sin(Math.min(groundTime,2.5)*.8)*.22,
      .03,
      -26.2-groundTime*2.35,
    );
    return target;
  }

  triggerImpact({silent=false}={}){
    this.impactTriggered=true;
    this.plate.visible=false;
    this.plateFrame.visible=false;
    this.impactFlash.material.opacity=1;
    this.impactFlash.scale.setScalar(1.5);
    this.impactLight.intensity=18;
    this.shards.forEach((shard)=>{
      const [a,b,c,d,e,f]=shard.random;
      const offsetX=shard.origin.x;
      const offsetY=shard.origin.y-1.2;
      shard.mesh.visible=true;
      shard.velocity.set(
        offsetX*.72+(a-.5)*2.2,
        1.4+Math.abs(offsetY)*.82+b*2.7,
        -3.2-c*5.2,
      );
      shard.spin.set((d-.5)*14,(e-.5)*18,(f-.5)*16);
    });
    this.sparks.forEach((spark)=>{
      const [a,b,c,d]=spark.random;
      const angle=a*Math.PI*2;
      const speed=3.5+b*8;
      spark.mesh.position.set((c-.5)*.9,.62+d*.75,PLATE_Z-.18);
      spark.velocity.set(Math.cos(angle)*speed,1.5+d*5,-2.2-Math.sin(angle)*speed*.7);
      spark.life=spark.maxLife;
      spark.mesh.material.opacity=1;
      spark.mesh.visible=true;
    });
    this.smoke.forEach((puff,index)=>{
      const [a,b,c,d]=puff.random;
      puff.mesh.position.set((a-.5)*1.6,.5+b*.95,PLATE_Z+(c-.5)*.6);
      puff.mesh.scale.setScalar(.35+d*.45);
      puff.mesh.material.opacity=.28;
      puff.mesh.visible=true;
      puff.delay=index*.035;
    });
    if(!silent){
      this.game.audio.noise(.24,.18,260,'lowpass');
      this.game.audio.blip(74,.3,'sawtooth',.1);
      this.game.audio.blip(145,.13,'square',.055);
    }
  }

  updateDebris(dt){
    if(!this.impactTriggered)return;
    const impactAge=this.time-IMPACT_TIME;
    this.shards.forEach((shard)=>{
      if(impactAge>1.7){
        shard.mesh.visible=false;
        return;
      }
      shard.velocity.y-=7.8*dt;
      shard.mesh.position.addScaledVector(shard.velocity,dt);
      shard.mesh.rotation.x+=shard.spin.x*dt;
      shard.mesh.rotation.y+=shard.spin.y*dt;
      shard.mesh.rotation.z+=shard.spin.z*dt;
      if(shard.mesh.position.y<.08){
        shard.mesh.position.y=.08;
        if(shard.velocity.y<0)shard.velocity.y*=-.34;
        shard.velocity.x*=.78;
        shard.velocity.z*=.78;
        shard.spin.multiplyScalar(.84);
      }
    });
    this.sparks.forEach((spark)=>{
      if(spark.life<=0)return;
      spark.life-=dt;
      spark.velocity.y-=8.5*dt;
      spark.mesh.position.addScaledVector(spark.velocity,dt);
      spark.mesh.material.opacity=Math.max(0,spark.life/spark.maxLife);
      spark.mesh.scale.setScalar(.55+spark.life/spark.maxLife);
      if(spark.life<=0)spark.mesh.visible=false;
    });
    this.smoke.forEach((puff)=>{
      const age=impactAge-puff.delay;
      if(age<0)return;
      const progress=clamp01(age/1.35);
      puff.mesh.position.y+=dt*(.34+puff.random[1]*.2);
      puff.mesh.position.z-=dt*(.28+puff.random[2]*.22);
      puff.mesh.scale.multiplyScalar(1+dt*(.7+puff.random[3]*.4));
      puff.mesh.material.opacity=.26*(1-progress);
      if(progress>=1)puff.mesh.visible=false;
    });
    const flashProgress=clamp01(impactAge/.34);
    this.impactFlash.material.opacity=(1-flashProgress)*.94;
    this.impactFlash.scale.setScalar(1.5+flashProgress*4.8);
    this.impactLight.intensity=18*(1-flashProgress);
  }

  updateDriftEffects(){
    this.driftPuffs.forEach((puff)=>{
      const age=this.time-puff.spawn;
      const visible=age>=0&&age<1.05;
      puff.mesh.visible=visible;
      if(!visible)return;
      this.sampleCarPosition(puff.spawn,puff.origin);
      const progress=age/1.05;
      puff.mesh.position.copy(puff.origin);
      puff.mesh.position.x+=puff.side*(.48+progress*.32);
      puff.mesh.position.y=.09+progress*.42;
      puff.mesh.position.z+=.58+progress*.8;
      puff.mesh.position.x+=Math.sin(puff.phase+progress*2.2)*.12;
      puff.mesh.scale.setScalar(.28+progress*1.2);
      puff.mesh.material.opacity=.46*(1-smooth(progress));
    });
  }

  updateSpeedLines(){
    const chaseProgress=clamp01((this.time-BOOST_TIME)/.45)*(1-smooth((this.time-OUTRO_START)/.8));
    this.speedLines.forEach(({line,index})=>{
      const visible=chaseProgress>0&&index%3!==Math.floor(this.time*18)%3;
      line.visible=visible;
      if(!visible)return;
      const side=index%2?-1:1;
      const lane=Math.floor(index/2)%5;
      line.position.set(
        this.car.position.x+side*(1.2+lane*.6),
        .08+(index%4)*.23,
        this.car.position.z-6+(index%10)*1.35,
      );
      line.material.opacity=.08+chaseProgress*.22;
    });
  }

  sampleFollowerPosition(time,follower,target){
    const sampleTime=Math.max(BOOST_TIME,time-follower.delay);
    this.sampleCarPosition(sampleTime,target);
    const entrance=smooth((time-(AIR_CHASE_START+follower.index*.17))/.7);
    target.x+=follower.lateral*(.5+.5*entrance);
    target.z+=2.25+follower.index*.72;
    if(time<LAND_TIME+follower.delay){
      target.y+=.22+Math.sin(time*2.1+follower.index)*.16;
    }else{
      target.y=.03;
    }
    return target;
  }

  sampleMissilePosition(missile,time,target){
    const progress=clamp01((time-missile.launch)/missile.duration);
    if(missile.primary){
      missile.scratchA.set(10.4,2.05,2.2);
      missile.scratchB.set(9.2,2.75,-.8);
      missile.scratchC.set(6.35,3.05,-4.25);
      missile.scratchD.set(4.05,3.18,-11.25);
      return cubicBezier(
        missile.scratchA,
        missile.scratchB,
        missile.scratchC,
        missile.scratchD,
        smooth(progress),
        target,
      );
    }
    this.sampleCarPosition(Math.max(BOOST_TIME,missile.launch-.5),missile.scratchA);
    missile.scratchA.x+=missile.lateral*1.15;
    missile.scratchA.y+=.2+missile.vertical;
    missile.scratchA.z+=4.5+missile.index*.12;
    this.sampleCarPosition(missile.launch+missile.duration,missile.scratchD);
    missile.scratchD.x+=missile.lateral;
    missile.scratchD.y+=missile.vertical;
    missile.scratchD.z-=4.2;
    missile.scratchB.copy(missile.scratchA).lerp(missile.scratchD,.32);
    missile.scratchB.y+=.7+Math.abs(missile.lateral)*.2;
    missile.scratchC.copy(missile.scratchA).lerp(missile.scratchD,.72);
    missile.scratchC.x-=missile.lateral*.62;
    missile.scratchC.y+=.35;
    return cubicBezier(
      missile.scratchA,
      missile.scratchB,
      missile.scratchC,
      missile.scratchD,
      smooth(progress),
      target,
    );
  }

  updatePursuitActors(dt){
    this.followers.forEach((follower)=>{
      const start=AIR_CHASE_START+follower.index*.17;
      const visible=this.time>=start&&this.time<OUTRO_START+.8;
      const wasVisible=follower.car.visible;
      follower.car.visible=visible;
      if(!visible)return;
      this.sampleFollowerPosition(this.time,follower,follower.car.position);
      this.sampleFollowerPosition(Math.min(SCENE_DURATION,this.time+FIXED_DT),follower,follower.nextPosition);
      if(!wasVisible)follower.lastPosition.copy(follower.car.position);
      tempDirection.copy(follower.nextPosition).sub(follower.car.position);
      const speed=follower.car.position.distanceTo(follower.lastPosition)/Math.max(dt,.0001);
      follower.distance+=speed*dt;
      follower.car.rotation.order='YXZ';
      follower.car.rotation.y=tempDirection.lengthSq()>.000001
        ?Math.atan2(-tempDirection.x,-tempDirection.z)
        :follower.car.rotation.y;
      follower.car.rotation.x=this.time<LAND_TIME?-.08:0;
      follower.car.rotation.z=Math.sin(this.time*1.5+follower.index)*.04;
      updateCarVisual(follower.car,{
        forwardSpeed:speed,
        steer:Math.sin(this.time+follower.index)*.32,
        grounded:this.time>=LAND_TIME+follower.delay,
        distance:follower.distance,
        boosting:true,
        groundHeight:0,
        findShadowSupportHeight:()=>0,
      },dt);
      follower.lastPosition.copy(follower.car.position);
      if((Math.floor(this.time*24)+follower.index*3)%5===0)this.game.effects.vehicleBoostSpark(follower.car);
    });

    this.missiles.forEach((missile)=>{
      const age=this.time-missile.launch;
      const visible=age>=0&&age<=missile.duration+.3&&this.time<OUTRO_START;
      missile.root.visible=visible;
      if(!visible)return;
      this.sampleMissilePosition(missile,this.time,missile.position);
      this.sampleMissilePosition(missile,this.time+FIXED_DT,missile.scratchNext);
      tempDirection.copy(missile.scratchNext).sub(missile.position).normalize();
      missile.root.position.copy(missile.position);
      missile.root.quaternion.setFromUnitVectors(projectileForward,tempDirection);
      const fadeOut=1-smooth((age-missile.duration)/.3);
      missile.trail.material.opacity=.72*fadeOut*(.76+Math.sin(this.time*72+missile.index)*.18);
      if((Math.floor(this.time*60)+missile.index*5)%4===0){
        this.game.effects.burst(
          missile.position.clone().addScaledVector(tempDirection,-.38),
          missile.index%2?'#ff9f32':'#68ddff',
          1,
          .45,
        );
      }
      missile.lastPosition.copy(missile.position);
    });
  }

  updateOutroOverlay(time){
    const backdropProgress=smooth((time-OUTRO_START)/.72);
    const logoProgress=smooth((time-(OUTRO_START+.42))/.78);
    const visible=backdropProgress>0;
    this.outroBackdrop.visible=visible;
    this.outroLogo.visible=visible;
    if(!visible)return;
    this.outroBackdrop.material.opacity=.96*backdropProgress;
    this.outroLogo.material.opacity=logoProgress;
    this.camera.getWorldDirection(tempDirection);
    this.outroBackdrop.position.copy(this.camera.position).addScaledVector(tempDirection,1.09);
    this.outroBackdrop.quaternion.copy(this.camera.quaternion);
    this.outroLogo.position.copy(this.camera.position).addScaledVector(tempDirection,1.04);
    this.outroLogo.quaternion.copy(this.camera.quaternion);
    const pulse=1+Math.sin((time-OUTRO_START)*2.2)*.012*logoProgress;
    this.outroLogo.scale.setScalar(pulse);
  }

  updateCamera(time){
    this.sampleCarPosition(time,tempPosition);
    this.sampleCarPosition(Math.min(SCENE_DURATION,time+FIXED_DT),tempNextPosition);
    tempDirectionNext.copy(tempNextPosition).sub(tempPosition).normalize();
    if(tempDirectionNext.lengthSq()<.000001)tempDirectionNext.set(0,0,-1);
    tempDirection.set(tempDirectionNext.z,0,-tempDirectionNext.x).normalize();
    let fov=50;
    if(time<IMPACT_TIME+.2){
      const push=smooth(time/1.9);
      tempCamera.set(.28,1.48,8.05+push*1.55);
      tempTarget.set(0,1.22,PLATE_Z);
      fov=48;
    }else if(time<4.62){
      const transition=smooth((time-(IMPACT_TIME+.2))/.62);
      const followCamera=tempPosition.clone()
        .addScaledVector(tempDirectionNext,-3.45)
        .addScaledVector(tempDirection,2.15);
      followCamera.y+=1.18;
      const followTarget=tempPosition.clone().addScaledVector(tempDirectionNext,1.55);
      followTarget.y+=.4;
      tempCamera.set(.28,1.48,9.6).lerp(followCamera,transition);
      tempTarget.set(0,1.22,PLATE_Z).lerp(followTarget,transition);
      fov=THREE.MathUtils.lerp(50,58,transition);
    }else if(time<6.34){
      tempCamera.copy(tempPosition)
        .addScaledVector(tempDirection,3.55)
        .addScaledVector(tempDirectionNext,-.7);
      tempCamera.y+=.82;
      tempTarget.copy(tempPosition).addScaledVector(tempDirectionNext,1.25);
      tempTarget.y+=.34;
      fov=61;
    }else if(time<7.48&&this.primaryMissile?.root.visible){
      const missilePosition=this.primaryMissile.root.position;
      tempCamera.copy(missilePosition).add(new THREE.Vector3(2.25,1.05,2.65));
      tempTarget.copy(missilePosition).add(new THREE.Vector3(-.5,.05,-1.5));
      fov=52;
    }else if(time<BOOST_TIME){
      tempCamera.copy(tempPosition)
        .addScaledVector(tempDirectionNext,-1.72)
        .addScaledVector(tempDirection,.26);
      tempCamera.y+=.53;
      tempTarget.copy(tempPosition).addScaledVector(tempDirectionNext,-.12);
      tempTarget.y+=.36;
      fov=43;
    }else if(time<AIR_CHASE_START){
      tempCamera.copy(tempPosition)
        .addScaledVector(tempDirection,.9)
        .addScaledVector(tempDirectionNext,-2.15);
      tempCamera.y+=.82;
      tempTarget.copy(tempPosition).addScaledVector(tempDirectionNext,.78);
      tempTarget.y+=.28;
      fov=58;
    }else if(time<LAND_TIME){
      tempCamera.set(11.8,4.65,-7.2);
      tempTarget.copy(tempPosition).addScaledVector(tempDirectionNext,1.25);
      tempTarget.y+=.45;
      fov=54;
    }else{
      const pullback=smooth((time-LAND_TIME)/(OUTRO_START-LAND_TIME));
      tempCamera.copy(tempPosition).addScaledVector(tempDirectionNext,-THREE.MathUtils.lerp(4.2,17,pullback));
      tempCamera.y+=THREE.MathUtils.lerp(1.45,5.2,pullback);
      tempTarget.copy(tempPosition).addScaledVector(tempDirectionNext,THREE.MathUtils.lerp(.85,-2.2,pullback));
      tempTarget.y+=.58;
      fov=THREE.MathUtils.lerp(59,52,pullback);
    }
    const impactAge=time-IMPACT_TIME;
    if(impactAge>=0&&impactAge<.52){
      const amplitude=(1-impactAge/.52)*.18;
      tempCamera.x+=Math.sin(impactAge*116)*amplitude;
      tempCamera.y+=Math.sin(impactAge*91+1.2)*amplitude*.62;
      tempCamera.z+=Math.sin(impactAge*137+.4)*amplitude*.45;
    }
    this.camera.position.copy(tempCamera);
    this.camera.lookAt(tempTarget);
    this.camera.fov=fov;
    this.camera.updateProjectionMatrix();
  }

  step(dt,{silent=false}={}){
    const previousTime=this.time;
    this.time=Math.min(SCENE_DURATION,this.time+dt);
    this.sampleCarPosition(this.time,tempPosition);
    this.sampleCarPosition(Math.min(SCENE_DURATION,this.time+dt),tempNextPosition);
    const forward=tempNextPosition.clone().sub(tempPosition);
    const speed=tempPosition.distanceTo(this.lastCarPosition)/Math.max(dt,.0001);
    this.distance+=speed*dt;
    const yaw=forward.lengthSq()>.000001?Math.atan2(-forward.x,-forward.z):this.car.rotation.y;
    const driftProgress=clamp01((this.time-DRIFT_START)/(JUMP_START-DRIFT_START));
    const driftAngle=this.time>=DRIFT_START&&this.time<JUMP_START
      ?Math.sin(driftProgress*Math.PI)*.42
      :0;
    const airborne=this.time>=JUMP_START&&this.time<LAND_TIME;
    const steer=THREE.MathUtils.clamp((yaw-this.car.rotation.y)*2.8+driftAngle*.8,-1,1);
    this.car.position.copy(tempPosition);
    this.car.rotation.order='YXZ';
    this.car.rotation.y=yaw+driftAngle;
    this.car.rotation.x=airborne
      ?THREE.MathUtils.lerp(-.04,-.16,smooth((this.time-JUMP_START)/(LAND_TIME-JUMP_START)))
      :0;
    this.car.rotation.z=airborne?Math.sin((this.time-JUMP_START)*1.25)*.055:0;
    this.lastCarPosition.copy(tempPosition);
    const boosting=this.time>=BOOST_TIME;
    updateCarVisual(this.car,{
      forwardSpeed:speed,
      steer,
      grounded:!airborne,
      distance:this.distance,
      boosting,
      groundHeight:0,
      findShadowSupportHeight:()=>0,
    },dt);
    this.boostClock-=dt;
    if(boosting&&this.boostClock<=0){
      this.boostClock=.024;
      this.game.effects.vehicleBoostSpark(this.car);
    }
    if(previousTime<IMPACT_TIME&&this.time>=IMPACT_TIME)this.triggerImpact({silent});
    if(!this.warningTriggered&&this.time>=SLOW_MOTION_START+.18){
      this.warningTriggered=true;
      if(!silent){
        this.game.audio.blip(620,.09,'square',.05);
        this.game.audio.blip(820,.07,'square',.04);
      }
    }
    if(!this.boostTriggered&&this.time>=BOOST_TIME){
      this.boostTriggered=true;
      if(!silent){
        this.game.audio.noise(.18,.12,1100,'highpass');
        this.game.audio.blip(95,.22,'sawtooth',.08);
      }
    }
    this.updateDebris(dt);
    this.updateDriftEffects();
    this.updateSpeedLines();
    this.updatePursuitActors(dt);
    this.game.effects.update(dt);
    this.updateCamera(this.time);
    this.updateOutroOverlay(this.time);
    if(!silent)this.game.audio.update(
      speed,
      boosting,
      true,
      0,
      this.car.userData.nitroEffect,
      this.car.userData.engineSound,
    );
  }

  update(dt){
    if(!this.ready){
      this.game.audio.update(0,false,false);
      return;
    }
    if(this.playing){
      this.accumulator+=Math.min(dt,.1);
      while(this.accumulator>=FIXED_DT){
        this.step(FIXED_DT);
        this.accumulator-=FIXED_DT;
      }
      if(this.time>=SCENE_DURATION){
        if(this.recording){
          this.playing=false;
          setTimeout(()=>this.stopRecording(),180);
        }else{
          this.playing=false;
          this.playButton.textContent='▶ REPETIR';
          this.setStatus('TOMA COMPLETA');
          this.game.audio.update(0,false,false);
        }
      }
    }else{
      this.game.audio.update(0,false,false);
    }
    this.updateInterface();
  }

  updateInterface(){
    if(!this.timelineElement)return;
    this.timelineElement.value=String(this.time);
    this.timeElement.textContent=`${this.time.toFixed(2)} / ${SCENE_DURATION.toFixed(2)}`;
  }

  togglePlay(){
    this.game.audio.ensure();
    if(this.time>=SCENE_DURATION)this.reset();
    else this.playing=!this.playing;
    this.playButton.textContent=this.playing?'❚❚ PAUSAR':'▶ REPRODUCIR';
    this.setStatus(this.playing?'REPRODUCIENDO':'ESCENA EN PAUSA');
  }

  seek(targetTime){
    const wasPlaying=this.playing;
    this.reset();
    this.playing=false;
    while(this.time+FIXED_DT<targetTime)this.step(FIXED_DT,{silent:true});
    if(targetTime>this.time)this.step(targetTime-this.time,{silent:true});
    this.playing=wasPlaying;
    this.playButton.textContent=this.playing?'❚❚ PAUSAR':'▶ REPRODUCIR';
    this.setStatus(this.playing?'REPRODUCIENDO':'VISTA PREVIA');
    this.updateInterface();
  }

  getRenderSize(){
    return this.recording?{width:3840,height:2160}:{width:window.innerWidth,height:window.innerHeight};
  }

  async toggleRecording(){
    if(this.recording){
      this.stopRecording();
      return;
    }
    if(typeof MediaRecorder==='undefined'||!this.game.canvas.captureStream){
      this.setStatus('EL NAVEGADOR NO PERMITE GRABAR EL CANVAS');
      return;
    }
    this.game.audio.ensure();
    this.recording=true;
    this.recordedChunks=[];
    this.recordButton.classList.add('active');
    this.recordButton.textContent='■ DETENER';
    this.renderer.setPixelRatio(1);
    this.renderer.setSize(3840,2160,false);
    this.camera.aspect=16/9;
    this.camera.updateProjectionMatrix();
    const canvasStream=this.game.canvas.captureStream(60);
    const tracks=[...canvasStream.getVideoTracks()];
    if(this.game.audio.context&&this.game.audio.master){
      this.captureAudioDestination=this.game.audio.context.createMediaStreamDestination();
      this.game.audio.master.connect(this.captureAudioDestination);
      tracks.push(...this.captureAudioDestination.stream.getAudioTracks());
    }
    const stream=new MediaStream(tracks);
    const preferred=['video/webm;codecs=vp9,opus','video/webm;codecs=vp8,opus','video/webm'];
    const mimeType=preferred.find((value)=>MediaRecorder.isTypeSupported(value))||'';
    this.mediaRecorder=new MediaRecorder(stream,{
      ...(mimeType?{mimeType}:{}),
      videoBitsPerSecond:45_000_000,
      audioBitsPerSecond:256_000,
    });
    this.mediaRecorder.addEventListener('dataavailable',(event)=>{
      if(event.data.size)this.recordedChunks.push(event.data);
    });
    this.mediaRecorder.addEventListener('stop',()=>this.finishRecording(stream,mimeType));
    this.mediaRecorder.start(250);
    this.reset();
    this.recording=true;
    this.playing=true;
    this.recordButton.classList.add('active');
    this.recordButton.textContent='■ DETENER';
    this.setStatus('GRABANDO 4K · TOMA EN CURSO');
  }

  stopRecording(){
    if(!this.recording)return;
    this.recording=false;
    if(this.mediaRecorder?.state!=='inactive')this.mediaRecorder.stop();
    this.recordButton.classList.remove('active');
    this.recordButton.textContent='● GRABAR 4K';
    this.setStatus('PROCESANDO CAPTURA…');
  }

  finishRecording(stream,mimeType){
    stream.getTracks().forEach((track)=>track.stop());
    if(this.captureAudioDestination&&this.game.audio.master){
      try{this.game.audio.master.disconnect(this.captureAudioDestination);}catch{}
    }
    this.captureAudioDestination=null;
    const blob=new Blob(this.recordedChunks,{type:mimeType||'video/webm'});
    const url=URL.createObjectURL(blob);
    const link=document.createElement('a');
    link.href=url;
    link.download='turbo-crown-cinematic-trailer-4k60.webm';
    link.click();
    setTimeout(()=>URL.revokeObjectURL(url),30_000);
    this.recordedChunks=[];
    this.mediaRecorder=null;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio,1.75));
    this.renderer.setSize(window.innerWidth,window.innerHeight,false);
    this.camera.aspect=window.innerWidth/window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.setStatus('CAPTURA DESCARGADA');
  }

  onKeyDown(event){
    if(event.repeat)return;
    if(event.code==='Space'){
      event.preventDefault();
      this.togglePlay();
    }else if(event.code==='KeyR'){
      event.preventDefault();
      this.reset();
    }else if(event.code==='KeyC'){
      event.preventDefault();
      this.toggleRecording();
    }
  }

  dispose(){
    window.removeEventListener('keydown',this.boundKeyDown);
    this.stopRecording();
    this.game.effects.clear();
    this.scene.remove(this.group);
    this.panel?.remove();
    this.panelStyle?.remove();
    this.renderer.toneMappingExposure=this.restoreExposure;
    this.game.sun.intensity=this.restoreSunIntensity;
    this.game.sun.position.copy(this.restoreSunPosition);
    this.game.audio.pauseMusic(false);
  }
}
